// hooks/table/configs/useSelectProductOnTransfer.ts
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import useUpdateEffect from 'src/hooks/useUpdateEffect';
import { useTableSettings, tableSettingsProps, useInitialTableSettings, useCompleteTableSettings } from '../tableSettings/useTableSettings';
import { useTableSelector, MergeResult } from '../tableSettings/useTableSelector';
import { useGetTransferProducts } from 'src/hooks/api/requestConfigs/warehouse';
import { ID_FIELD_PRODUCTS } from 'src/utils/idFields';
import tableHelper from 'src/utils/tableHelper';

export interface TransferProduct {
  ProductId: number | string;
  ProductName: string;
  ManufacturerProductId?: string;
  ProductStatus_Key?: any;
  ProductStatus_Value?: any;
  WarehouseQty?: number;
  WarehouseReservedQty?: number;
  DispatchQty?: number;
  DispatchCmdQty?: number;
  WarehouseAvailableQty?: number;
  ToWarehouseAvailableQty?: number;
}

const EMPTY_PRODUCTS: TransferProduct[] = [];

const getWarehouseId = (v: any): number | string | null => {
  if (v === undefined || v === null) return null;
  if (typeof v === 'number' || typeof v === 'string') return v;
  if (v?.WarehouseId !== undefined) return v.WarehouseId;
  if (v?.Id !== undefined) return v.Id;
  if (v?.Key !== undefined) return v.Key;
  return null;
};

export const useTransferOrderRowsTable = (
  rows: any[],
  setRows: React.Dispatch<React.SetStateAction<any[]>>,
  orderId: number | string,
): readonly [GridColDef[], tableSettingsProps] => {
  const newTableNameRef = useRef(`TransferOrderRowsTable_new_${Math.random()}`);

  const tableName =
    orderId != null
      ? `TransferOrderRowsTable_${orderId}`
      : newTableNameRef.current;

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'RowNr', headerName: '#', width: 40, type: 'number' },
      { field: 'OrderRowId', headerName: 'Order Row Id', renderCell: tableHelper.idCell },
      { field: 'Product', headerName: 'Product Name', flex: 1 },
      { field: 'ProductId', headerName: 'Product Id' },
      { field: 'Qty', headerName: 'Qty', type: 'number', editable: true },
      { field: 'QtyTransferred', headerName: 'Qty Transferred', type: 'number' },
      { field: 'AvailableQtyAtTime', headerName: 'Available Qty At Time', type: 'number' },
      { field: 'Comment', headerName: 'Comment', editable: true, flex: 1 },
      {
        field: 'actions',
        getActions: (params: GridRowParams) => tableHelper.deleteButton(params, setRows, rows, tableSettings),
        type: 'actions',
        headerName: 'Delete',
        minWidth: 80,
        flex: 0.8,
      },
    ],
    [rows, setRows],
  );

  const initialTableSettings = useInitialTableSettings(
    {
      tableName: tableName,
      idPath: ['OrderRowId'],
    },
    rows,
  );

  const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);
  tableSettings.autoHeight = true;
  tableSettings.hasToolbar = false;
  tableSettings.loading = false;

  return [columns, tableSettings] as const;
};

export const useTransferProductsSelectTable = (
  fromWarehouseId: number | string | null,
  toWarehouseId?: number | string | null,
): readonly [GridColDef[], tableSettingsProps] => {

  const { rows: fromRowsRaw, status: fromStatus } = useGetTransferProducts(fromWarehouseId);
  const { rows: toRowsRaw, status: toStatus } = useGetTransferProducts(toWarehouseId);

  const fromRows = useMemo(() => (Array.isArray(fromRowsRaw) ? (fromRowsRaw as TransferProduct[]) : EMPTY_PRODUCTS), [fromRowsRaw]);
  const toRows = useMemo(() => (Array.isArray(toRowsRaw) ? (toRowsRaw as TransferProduct[]) : EMPTY_PRODUCTS), [toRowsRaw]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'ProductId', headerName: 'Id', width: 75, maxWidth: 85, hideable: false },
      { field: 'ProductName', headerName: 'Name', minWidth: 180, width: 190, flex: 2 },
      {
        field: 'ProductStatus_Value',
        headerName: 'Status',
        cellClassName: tableHelper.statusColumn,
        valueGetter: tableHelper.getFieldKeyOrValue,
        renderCell: tableHelper.renderStatusCell,
      },
      { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id' },
      //Manufacturer
      {field:"Manufacturer_Value", headerName:"Manufacturer", valueGetter: tableHelper.getFieldKeyOrValue, },
      { field: 'WarehouseQty', headerName: 'Warehouse Qty', type: 'number', width: 200 },
      { field: 'WarehouseAvailableQty', headerName: 'Available Qty', type: 'number' },
      { field: 'ToWarehouseAvailableQty', headerName: 'Reciving Warehouse Availible Qty', type: 'number', width: 200 },
      { field: 'WarehouseReservedQty', headerName: 'Reserved Qty', type: 'number' },
      { field: 'DispatchQty', headerName: 'Dispatch Qty', type: 'number' },
      { field: 'DispatchCmdQty', headerName: 'Dispatch Cmd Qty', type: 'number' },
      {
        field: 'ProductStatus_Key',
        headerName: 'Status Id',
        valueGetter: tableHelper.getFieldKeyOrValue,
        width: 80,
        minWidth: 70,
        maxWidth: 100,
      },
    ],
    [],
  );

  const mergedRows = useMemo(() => {
    const toByProductId = new Map<number | string, TransferProduct>();
    for (const r of toRows) {
      if (r?.ProductId === undefined || r?.ProductId === null) continue;
      toByProductId.set(r.ProductId, r);
    }

    return fromRows.map((r) => {
      const match = toByProductId.get(r.ProductId);
      return { ...r, ToWarehouseAvailableQty: match?.WarehouseAvailableQty ?? 0 };
    });
  }, [fromRows, toRows]);

  const status = useMemo(() => {
    if (fromStatus === 'loading' || toStatus === 'loading') return 'loading' as any;
    if (fromStatus === 'error' || toStatus === 'error') return 'error' as any;
    return fromStatus as any;
  }, [fromStatus, toStatus]);

  const tableSettings = useTableSettings({
    columns,
    rows: mergedRows,
    status,
    idPath: ID_FIELD_PRODUCTS,
    tableName: `TransferProductSelectTable_${fromWarehouseId}_${String(toWarehouseId)}`,
  });

  // Initial sort: Available Qty (From warehouse) desc
  tableSettings.initalSorting = [{ field: 'WarehouseAvailableQty', sort: 'desc' }];

  useEffect(() => {
    tableSettings.setRowSelectionModel([]);
  }, [fromWarehouseId ? String(toWarehouseId) : 'none']);

  tableSettings.checkboxSelection = true;
  tableSettings.hasToolbar = false;

  return [columns, tableSettings] as const;
};

export function useSelectProductOnTransfer(initialValues: any, saveResult?: any): [
  GridColDef[],
  tableSettingsProps,
  any[],
  ReturnType<typeof useTableSelector<any>>,
] {
  const [rows, setRows] = useState<any[]>(initialValues?.TransferOrderRows ?? []);

  const [fromWarehouseId, setFromWarehouseId] = useState<number | string | null>(() => {
    return getWarehouseId(initialValues?.FromWarehouse) ?? getWarehouseId(initialValues?.FromWarehouseId);
  });

  const toWarehouseId = useMemo(() => {
    return getWarehouseId(initialValues?.ToWarehouse) ?? getWarehouseId(initialValues?.ToWarehouseId);
  }, [initialValues?.ToWarehouse, initialValues?.ToWarehouseId]);

  const [dispCols, dispSettings] = useTransferOrderRowsTable(rows, setRows, initialValues?.OrderId);
  const [selCols, selSettings] = useTransferProductsSelectTable(fromWarehouseId, toWarehouseId);

  // We only need this to coordinate a "hard reset" after save
  const pendingTableResetRef = useRef(false);

  useUpdateEffect(() => {
    if (initialValues?.TransferOrderRows !== undefined) setRows(initialValues.TransferOrderRows);
  }, [initialValues?.TransferOrderRows]);

  useUpdateEffect(() => {
    const next = getWarehouseId(initialValues?.FromWarehouse) ?? getWarehouseId(initialValues?.FromWarehouseId);
    if (next !== undefined) setFromWarehouseId(next);
  }, [initialValues?.FromWarehouse, initialValues?.FromWarehouseId]);

  // On successful save: replace external rows with server truth and clear selection
  useUpdateEffect(() => {
    if (!saveResult) return;

    const savedRows = saveResult?.TransferOrderRows;

    pendingTableResetRef.current = true;

    setRows(Array.isArray(savedRows) ? savedRows : []);

    dispSettings?.setRowSelectionModel?.([]);
    selSettings?.setRowSelectionModel?.([]);
  }, [saveResult]);

  // After the rows swap has committed, wipe table's internal edit overlay and force grid rows
  useEffect(() => {
    if (!pendingTableResetRef.current) return;
    pendingTableResetRef.current = false;

    // Clear internal edits (changedRowsRef + persisted edits)
    dispSettings?.discardAllChanges?.();

    // Force DataGrid to adopt the new row truth (clears any internal row cache)
    dispSettings?.apiRef?.current?.setRows?.(rows);

    // Keep selection cleared
    dispSettings?.setRowSelectionModel?.([]);
    selSettings?.setRowSelectionModel?.([]);
  }, [rows, dispSettings, selSettings]);

  const mergeProductsIntoRows = (prev: any[], picked: TransferProduct[]): MergeResult<any> => {
    if (!picked.length) return prev;

    const next = [...prev];

    picked.forEach((prod) => {
      const rowNr = next.length + 1;
      const tempId = -rowNr;

      next.push({
        RowNr: rowNr,
        OrderRowId: tempId,
        Product: prod.ProductName,
        ProductId: prod.ProductId,
        Qty: 1,
        QtyTransferred: 0,
        AvailableQtyAtTime: prod.WarehouseAvailableQty ?? 0,
        Comment: '',
      });
    });

    return next;
  };

  const selector = useTableSelector<any>({
    rows,
    setRows,
    selectGrid: [selCols, selSettings],
    getId: (row) => row.ProductId,
    onMerge: mergeProductsIntoRows,
    rowId: 'OrderRowId',
    displayTableSettings: dispSettings,
  });

  return [dispCols, dispSettings, rows, selector];
}